// @flow
import type { Node } from 'react';
import React, { useState, useEffect, useContext } from 'react';
import httpService from '../../services/http';
import { isEmpty } from '../../utils/helper/helper';
import { useStructureContext } from '../StructureProvider';

type Props = {
  children: Node,
};

type contextType = {
  isLoading: boolean,
  setIsLoading: any,
  refrigerantList: any,
  refrigerantSelectList: any,
  gasList: any,
  gasSelectList: any,
  getMediaDataRefrigerant: any,
  getMediaDataMoistAir: any,
  parameter: any,
  setParameter: any,
  setInitialParameter: any,
};

export const UIParameterContext: React$Context<contextType> = React.createContext<contextType>({
  isLoading: true,
  setIsLoading: () => {},
  refrigerantList: [],
  refrigerantSelectList: [],
  gasList: [],
  gasSelectList: [],
  getMediaDataRefrigerant: () => {},
  getMediaDataMoistAir: () => {},
  parameter: null,
  setParameter: () => {},
  setInitialParameter: () => {},
});

const UIParameterProvider = ({ children }: Props): Node => {
  const [isLoading, setIsLoading] = useState(true);
  const [refrigerantList, setRefrigerantList] = useState([]);
  const [refrigerantSelectList, setRefrigerantSelectList] = useState([]);
  const [gasList, setGasList] = useState([]);
  const [gasSelectList, setGasSelectList] = useState([]);
  const [parameter, setParameter] = useState([]);
  const [cachedFluidID, setCachedFluidID] = useState(null);
  const [cachedGasID, setCachedGasID] = useState(null);
  const [fluidData, setFluidData] = useState({});
  const [gasData, setGasData] = useState({});

  const structureContext = useStructureContext();

  /**
   * Function can makes sure that old simulations work with up-to-date parameter structure
   * If no parameters are passed, default initial parameter are set.
   * @param parameter list of parameters
   * @returns {*}
   */
  const setInitialParameter = (parameters?: any = {}) => {
    let initialValues = {};
    ({ initialValues } = structureContext);
    const withInitial = { ...initialValues, ...parameters };
    setParameter([withInitial]);

    if (!isEmpty(withInitial)) setIsLoading(false);
  };

  /**
   * @description is called to get list of available refrigerants
   */
  const getRefrigerantList = () => {
    httpService
      .get('fluiddata/logphstatechart/', {}, 'json', true)
      .then((response) => {
        const listRefrigerants = response.data;
        setRefrigerantList(listRefrigerants);

        // construct an array for select component with id and name
        const selectList = [];
        listRefrigerants.map((l) => {
          selectList.push({ value: l.id, label: l.name });
          return true;
        });
        setRefrigerantSelectList(selectList);

        return true;
      })
      .catch((error) => error);
  };

  /**
   * @description is called to get list of available gases
   */
  const getGasList = () => {
    httpService
      .get('fluiddata/hxstatechart/', {}, 'json', true)
      .then((response) => {
        const listGases = response.data;
        setGasList(listGases);

        // construct an array for select component with id and name
        const selectList = [];
        listGases.map((l) => {
          selectList.push({ value: l.id, label: l.name });
          return true;
        });
        setGasSelectList(selectList);
        return true;
      })
      .catch((error) => error);
  };

  // TODO: memoize the mediaData
  /**
   * @description is called to get media data of one refrigerant,
   * if no id is given, the first FMU in the list is returned
   * @param id of fmu
   */
  /* eslint-disable */
  const getMediaDataRefrigerant = (id?: number) =>
    new Promise((resolve, reject) => {
      const logphStateChartDataPlot = {};
      if (!(refrigerantList.length < 1) && !(id === cachedFluidID)) {
        const idRefrigerant = id || refrigerantList[0].id;
        setCachedFluidID(idRefrigerant);
        httpService
          .get(`fluiddata/logphstatechart/${idRefrigerant}/`, {}, 'json', true)
          .then((response) => {
            const logphStateChartData = response.data;
            logphStateChartDataPlot.name = logphStateChartData.name;
            logphStateChartDataPlot['plot_settings'] = logphStateChartData['plot_settings'];
            logphStateChartDataPlot.vaporDome = logphStateChartData.vaporDome;
            logphStateChartDataPlot.TIsolines = logphStateChartData.TIsolines;
            logphStateChartDataPlot.xIsolines = logphStateChartData.xIsolines;
            logphStateChartDataPlot.vIsolines = logphStateChartData.vIsolines;
            logphStateChartDataPlot.sIsolines = logphStateChartData.sIsolines;
            setFluidData(logphStateChartDataPlot);
            resolve(logphStateChartDataPlot);
            return true;
          })
          .catch(() => {
            reject({
              name: 'Error: Refrigerant data currently not available',
              vaporDome: [],
              TIsolines: [],
              xIsolines: [],
              sIsolines: [],
              vIsolines: [],
            });
            return false;
          });
      } else {
        resolve(fluidData);
        return true;
      }
    });
  /* eslint-enable */

  /**
   * @description is called to get media data of one refrigerant,
   * if no id is given, the first FMU in the list is returned
   * @param id of fmu
   */
  /* eslint-disable */
  const getMediaDataMoistAir = (id?: number) =>
    new Promise((resolve, reject) => {
      const hxStateChartDataPlot = {};
      if (!(id === cachedGasID)) {
        const idGas = id || 1;
        setCachedGasID(idGas);
        httpService
          .get(`fluiddata/hxstatechart/${idGas}/`, {}, 'json', true)
          .then((response) => {
            const hxStateChartData = response.data;
            hxStateChartDataPlot.name = hxStateChartData.name;
            hxStateChartDataPlot['plot_settings'] = hxStateChartData['plot_settings'];
            hxStateChartDataPlot.h1pxIsolines = hxStateChartData['h_isolines'];
            hxStateChartDataPlot.TIsolines = hxStateChartData['t_isolines'];
            hxStateChartDataPlot.phiIsolines = hxStateChartData['phi_isolines'];
            hxStateChartDataPlot.areaUnsaturatedAir = hxStateChartData['area_unsaturated_air'];
            setGasData(hxStateChartDataPlot);
            resolve(hxStateChartDataPlot);
            return true;
          })
          .catch(() => {
            reject({
              name: 'Error: Moist Air data currently not available',
              TIsolines: [],
              h1pxIsolines: [],
              phiIsolines: [],
              areaUnsaturatedAir: [],
            });
            return false;
          });
      } else {
        resolve(gasData);
        return true;
      }
    });
  /* eslint-enable */

  useEffect(() => {
    getRefrigerantList();
    getGasList();
  }, []);

  useEffect(() => {
    setInitialParameter();
  }, [structureContext.isLoadingStructure]);

  return (
    <UIParameterContext.Provider
      value={{
        refrigerantList,
        refrigerantSelectList,
        gasList,
        gasSelectList,
        getMediaDataRefrigerant,
        getMediaDataMoistAir,
        isLoading,
        setIsLoading,
        parameter,
        setParameter,
        setInitialParameter,
      }}
    >
      {children}
    </UIParameterContext.Provider>
  );
};

export const useUIParameterContext = (): contextType => {
  const context = useContext(UIParameterContext);
  if (context === undefined) {
    throw new Error('useUIParameterContext must be used within a UIParameterProvider');
  }
  return context;
};

export default UIParameterProvider;
